import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { UserAuthForm } from "@/components/ui/user-auth-form";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/components/ui/use-toast";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { getInvitation } from "@/services/invitationService";
import { validateRegistrationCode } from "@/services/registrationCodeService";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, Lock } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [registrationType, setRegistrationType] = useState<"user" | "company">("user");
  const [invitedEmail, setInvitedEmail] = useState<string>("");
  const [invitedTenantId, setInvitedTenantId] = useState<string | undefined>(undefined);
  const [loadingInvitation, setLoadingInvitation] = useState(false);

  // Registration code state
  const [registrationCode, setRegistrationCode] = useState("");
  const [codeValidated, setCodeValidated] = useState(false);
  const [validatingCode, setValidatingCode] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);

  const invitationId = searchParams.get("invitationId");

  useEffect(() => {
    if (invitationId) {
      checkInvitation(invitationId);
    }
  }, [invitationId]);

  const checkInvitation = async (id: string) => {
    setLoadingInvitation(true);
    try {
      const invitation = await getInvitation(id);
      if (invitation) {
        if (invitation.status !== 'pending') {
          setError("Diese Einladung ist nicht mehr gültig.");
          return;
        }
        setInvitedEmail(invitation.inviteeEmail);
        setInvitedTenantId(invitation.tenantId);
        setRegistrationType("user"); // Force user registration
        toast({
          title: "Einladung gefunden",
          description: `Sie wurden eingeladen, dem Team beizutreten.`,
        });
      } else {
        setError("Einladung nicht gefunden.");
      }
    } catch (err) {
      console.error("Error fetching invitation:", err);
      setError("Fehler beim Laden der Einladung.");
    } finally {
      setLoadingInvitation(false);
    }
  };

  const handleCodeValidation = async (e: React.FormEvent) => {
    e.preventDefault();
    setCodeError(null);
    setValidatingCode(true);

    try {
      const isValid = await validateRegistrationCode(registrationCode);
      if (isValid) {
        setCodeValidated(true);
        toast({
          title: "Code akzeptiert",
          description: "Sie können jetzt Ihr Konto erstellen.",
        });
      } else {
        setCodeError("Ungültiger Registrierungscode");
      }
    } catch (err) {
      setCodeError("Fehler bei der Code-Validierung");
    } finally {
      setValidatingCode(false);
    }
  };

  const handleRegister = async (data: { email: string; password: string; name?: string; companyName?: string }) => {
    try {
      if (!data.name) {
        throw new Error("Name ist erforderlich");
      }

      if (registrationType === "company" && !data.companyName) {
        throw new Error("Firmenname ist erforderlich");
      }

      // If invited, ensure email matches (optional, but good for security/consistency)
      if (invitedEmail && data.email.toLowerCase() !== invitedEmail.toLowerCase()) {
        // We could block it, or just warn. Let's allow it but use the invited tenantId.
        // Actually, if they change email, they might not be the person invited. 
        // But for now, let's just use the tenantId from the invite if present.
      }

      await register(data.name, data.email, data.password, {
        companyName: registrationType === "company" ? data.companyName : undefined,
        tenantId: invitedTenantId // Pass the tenantId from invitation
      });

      toast({
        title: "Registrierung erfolgreich",
        description: "Sie können sich jetzt anmelden.",
      });
      navigate("/login");
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Ein unbekannter Fehler ist aufgetreten.");
      }
      // Fehlermeldung wird bereits im Formular angezeigt
      return Promise.reject(err);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Left side - illustration */}
      <div className="hidden lg:flex flex-1 bg-primary items-center justify-center p-8">
        <div className="max-w-md text-white">
          <h1 className="text-3xl font-bold mb-6">LeadsPro - Ihr Lead Management System</h1>
          <p className="text-lg mb-4">
            Erstellen Sie ein Konto und starten Sie noch heute mit der effizienten Verwaltung Ihrer Leads!
          </p>
          <ul className="space-y-2 mb-6">
            <li className="flex items-center">
              <div className="rounded-full bg-white/20 p-1 mr-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
              Problemlose Lead-Verwaltung
            </li>
            <li className="flex items-center">
              <div className="rounded-full bg-white/20 p-1 mr-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
              Detaillierte Reporting-Funktionen
            </li>
            <li className="flex items-center">
              <div className="rounded-full bg-white/20 p-1 mr-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
              DSGVO-konforme Datenspeicherung
            </li>
          </ul>
        </div>
      </div>

      {/* Right side - registration form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-6">
          {/* Show code entry if not validated and no invitation */}
          {!codeValidated && !invitationId ? (
            <>
              <div className="text-center mb-8">
                <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <Lock className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-2xl font-bold">Registrierungscode</h2>
                <p className="text-muted-foreground mt-2">
                  Bitte geben Sie den Registrierungscode ein, um fortzufahren
                </p>
              </div>

              <form onSubmit={handleCodeValidation} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="registrationCode">Code</Label>
                  <Input
                    id="registrationCode"
                    type="text"
                    placeholder="Registrierungscode eingeben"
                    value={registrationCode}
                    onChange={(e) => setRegistrationCode(e.target.value)}
                    disabled={validatingCode}
                    className="text-center text-lg tracking-widest"
                  />
                </div>

                {codeError && (
                  <div className="bg-destructive/15 text-destructive px-4 py-3 rounded-md text-sm">
                    {codeError}
                  </div>
                )}

                <Button type="submit" className="w-full" disabled={validatingCode || !registrationCode}>
                  {validatingCode ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Wird überprüft...
                    </>
                  ) : (
                    "Code überprüfen"
                  )}
                </Button>
              </form>

              <div className="text-center text-sm">
                <p className="text-muted-foreground">
                  Bereits ein Konto?{" "}
                  <Link to="/login" className="text-primary hover:underline">
                    Anmelden
                  </Link>
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold">Konto erstellen</h2>
                <p className="text-muted-foreground mt-2">
                  Geben Sie Ihre Daten ein, um ein Konto zu erstellen
                </p>
              </div>

              <Tabs defaultValue="user" value={registrationType} onValueChange={(val) => !invitedTenantId && setRegistrationType(val as "user" | "company")} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="user">Als Benutzer</TabsTrigger>
                  <TabsTrigger value="company" disabled={!!invitedTenantId}>Als Firma</TabsTrigger>
                </TabsList>

                {error && (
                  <div className="bg-destructive/15 text-destructive px-4 py-3 rounded-md text-sm mb-4">
                    {error}
                  </div>
                )}

                <TabsContent value="user">
                  <UserAuthForm
                    type="register"
                    onSubmit={handleRegister}
                    showCompanyField={false}
                    defaultEmail={invitedEmail}
                  />
                </TabsContent>

                <TabsContent value="company">
                  <UserAuthForm type="register" onSubmit={handleRegister} showCompanyField={true} />
                </TabsContent>
              </Tabs>

              <div className="text-center text-sm">
                <p className="text-muted-foreground">
                  Bereits ein Konto?{" "}
                  <Link to="/login" className="text-primary hover:underline">
                    Anmelden
                  </Link>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
